import React, { useEffect } from 'react';
import { X, FileText, Shield, ExternalLink, Printer } from 'lucide-react';
import { useDialogA11y } from '../../lib/useDialogA11y';

export type LegalDocType = 'terms' | 'privacy';

interface LegalTermsModalProps {
  initialDoc?: LegalDocType;
  isOpen: boolean;
  onClose: () => void;
}

export default function LegalTermsModal({
  initialDoc = 'terms',
  isOpen,
  onClose,
}: LegalTermsModalProps) {
  const [activeDoc, setActiveDoc] = React.useState<LegalDocType>(initialDoc);

  useEffect(() => {
    setActiveDoc(initialDoc);
  }, [initialDoc]);

  const { ref: dialogRef } = useDialogA11y({ open: isOpen, onClose });

  if (!isOpen) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      ref={dialogRef}
      className="legal-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 'calc(var(--z-popover) + 5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.2s ease',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="legal-modal-sheet"
        style={{
          backgroundColor: '#0d1811',
          border: '1px solid rgba(74, 222, 128, 0.25)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '820px',
          maxHeight: '90dvh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
          color: '#e2e8f0',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            backgroundColor: '#14251a',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveDoc('terms')}
              style={{
                minHeight: '40px',
                padding: '0 14px',
                borderRadius: '8px',
                backgroundColor: activeDoc === 'terms' ? 'rgba(74, 222, 128, 0.18)' : 'transparent',
                border:
                  activeDoc === 'terms'
                    ? '1px solid rgba(74, 222, 128, 0.4)'
                    : '1px solid transparent',
                color: activeDoc === 'terms' ? '#4ade80' : '#94a3b8',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FileText size={16} />
              Brugerbetingelser
            </button>
            <button
              type="button"
              onClick={() => setActiveDoc('privacy')}
              style={{
                minHeight: '40px',
                padding: '0 14px',
                borderRadius: '8px',
                backgroundColor:
                  activeDoc === 'privacy' ? 'rgba(74, 222, 128, 0.18)' : 'transparent',
                border:
                  activeDoc === 'privacy'
                    ? '1px solid rgba(74, 222, 128, 0.4)'
                    : '1px solid transparent',
                color: activeDoc === 'privacy' ? '#4ade80' : '#94a3b8',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Shield size={16} />
              Privatlivspolitik (GDPR)
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handlePrint}
              title="Udskriv vilkår"
              aria-label="Udskriv vilkår"
              style={{
                minWidth: '40px',
                minHeight: '40px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#cbd5e1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Printer size={16} />
            </button>
            <button
              type="button"
              onClick={onClose}
              title="Luk"
              aria-label="Luk modal"
              style={{
                minWidth: '40px',
                minHeight: '40px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#cbd5e1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div
          style={{
            padding: '24px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            fontSize: '0.9rem',
            lineHeight: '1.6',
            color: '#cbd5e1',
          }}
        >
          {activeDoc === 'terms' ? (
            <div>
              <h2
                id="legal-modal-title"
                style={{
                  color: '#ffffff',
                  fontSize: '1.35rem',
                  fontWeight: 700,
                  marginTop: 0,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FileText size={22} color="#4ade80" />
                Brugerbetingelser & Ansvarsafgrænsning
              </h2>
              <p
                style={{
                  fontSize: '0.82rem',
                  color: '#94a3b8',
                  marginBottom: '20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                  paddingBottom: '12px',
                }}
              >
                Gældende version v1.0 · Senest opdateret: Oktober 2026
              </p>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>1. Parterne</h3>
                <p>
                  Disse brugerbetingelser gælder for brug af webplatformen Plotwise (
                  <a
                    href="https://plotwise.dk"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#4ade80' }}
                  >
                    https://plotwise.dk
                  </a>
                  ), som udbydes og drives af:
                </p>
                <div
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    fontSize: '0.85rem',
                  }}
                >
                  <strong>Virksomhed:</strong> EMService V/Emil Mortensen
                  <br />
                  <strong>CVR-nummer:</strong> 44220695
                  <br />
                  <strong>Forretningsadresse:</strong> Vognmandsvej 1, 4480 Store Fuglede
                  <br />
                  <strong>Kontakt e-mail:</strong>{' '}
                  <a href="mailto:Emilbeiner@gmail.com" style={{ color: '#4ade80' }}>
                    Emilbeiner@gmail.com
                  </a>
                </div>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  2. Ydelsens karakter & Formål
                </h3>
                <p>
                  Plotwise er et digitalt planlægnings- og skitseværktøj til jægere, godsejere og
                  vildtforvaltere. Platformen stiller ortofotos, matrikelskel og vejledende
                  arealberegningsmodeller til rådighed. <strong>Alle beregninger</strong> af
                  markarealer, frømængder og biotopplaner er vejledende overslag udregnet efter
                  officielle tommelfingerregler.
                </p>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  3. Brugerens kontrolpligt forud for såning
                </h3>
                <p>
                  Brugeren bærer selv det fulde, selvstændige ansvar for kontrol og verifikation af
                  de faktiske forhold i marken forud for enhver såning, gødskning eller
                  jordbehandling. Dette omfatter bl.a.:
                </p>
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                  <li>Fysisk opmåling og verifikation af markens reelle grænser og areal.</li>
                  <li>Jordbundstype, jordtemperatur, fugtighed og såbedets tilstand.</li>
                  <li>
                    Korrekt kalibrering og indstilling af såmaskiner og sådybde. (Bemærk bl.a., at
                    Hunters Corn måles i 100.000 kerner/ha og ikke i kg).
                  </li>
                  <li>
                    Overholdelse af gældende lovgivning, herunder GLM-krav, §3 beskyttet natur og
                    BNBO.
                  </li>
                </ul>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  4. Forholdet til Faunamaster ApS & Frøkøb
                </h3>
                <p>
                  Plotwise og EMService er <strong>ikke</strong> forhandler eller sælger af frø,
                  maskiner eller fysiske varer. Indkøbskurven i Plotwise udgør en digital service,
                  der automatisk viderestiller brugeren til <strong>Faunamaster ApS</strong>’
                  webshop (med 5 % rabatkode <code>BEINER</code>).
                </p>
                <p>
                  Enhver aftale om køb, betaling, levering og reklamation indgås udelukkende og
                  direkte mellem brugeren og Faunamaster ApS på Faunamaster ApS’ egne
                  handelsbetingelser.
                </p>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  5. Betaling & Abonnementsvilkår (Stripe)
                </h3>
                <p>
                  Adgang til udvidede moduler (Semi-pro og Professionel) faktureres som løbende
                  abonnement via Stripe. Priser oplyses i DKK inkl. moms. Abonnementet fornyes
                  automatisk månedsvis eller årligt, indtil det opsiges. Opsigelse kan ske til
                  enhver tid med virkning fra udløbet af den forudbetalte periode.
                </p>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  6. Fortrydelsesret ved digitale tjenester
                </h3>
                <p>
                  For forbrugere bortfalder den almindelige 14 dages fortrydelsesret jf.
                  Forbrugeraftalelovens § 18, stk. 2, nr. 13, så snart leveringen af det digitale
                  indhold eller den digitale softwarepåbegyndes efter brugerens forudgående,
                  udtrykkelige samtykke ved oprettelse og login.
                </p>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  7. Flerårig Datagaranti
                </h3>
                <p>
                  Dine data tilhører dig. Hvis du opsiger et betalt abonnement, slettes dine
                  marktegninger, revirdata og plejeplaner <strong>aldrig</strong>. Din konto overgår
                  automatisk til en gratis visningstilstand, så du altid har adgang til dine
                  historiske kort og planer.
                </p>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  8. Ansvarsfraskrivelse & Erstatningsbegrænsning
                </h3>
                <p>
                  Plotwise stilles til rådighed »som den er og forefindes«. EMService fraskriver sig
                  ethvert ansvar for:
                </p>
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                  <li>
                    Udebleven eller mangelfuld spiring af afgrøder, frøblandinger eller vildtagre.
                  </li>
                  <li>Vejrforhold, tørke, frost, oversvømmelse, fugle- eller vildtskader.</li>
                  <li>Fejlindstillinger af såmaskiner, redskaber eller forkert udsædsmængde.</li>
                  <li>
                    Indirekte tab, driftstab, tabt avance, mistede jagtlejeindtægter eller
                    følgeskader.
                  </li>
                </ul>
                <p>
                  EMServices samlede erstatningsansvar over for brugeren kan under ingen
                  omstændigheder overstige det beløb, brugeren har betalt for adgang til Plotwise i
                  de seneste 12 måneder, dog maksimalt <strong>DKK 1.000,-</strong>. For gratis
                  brugere er ansvaret maksimeret til DKK 0,-.
                </p>
              </section>

              <section style={{ marginBottom: '10px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  9. Lovvalg & Værneting
                </h3>
                <p>
                  Enhver tvist, der måtte udspringe af nærværende betingelser eller brugen af
                  Plotwise, skal afgøres efter dansk ret ved de almindelige danske domstole med{' '}
                  <strong>Retten i Holbæk</strong> som aftalt værneting i første instans.
                </p>
              </section>
            </div>
          ) : (
            <div>
              <h2
                id="legal-modal-title"
                style={{
                  color: '#ffffff',
                  fontSize: '1.35rem',
                  fontWeight: 700,
                  marginTop: 0,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Shield size={22} color="#4ade80" />
                Privatlivspolitik (GDPR)
              </h2>
              <p
                style={{
                  fontSize: '0.82rem',
                  color: '#94a3b8',
                  marginBottom: '20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                  paddingBottom: '12px',
                }}
              >
                Gældende version v1.0 · Senest opdateret: Oktober 2026
              </p>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  1. Dataansvarlig
                </h3>
                <p>Dataansvarlig for behandlingen af dine personoplysninger er:</p>
                <div
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    fontSize: '0.85rem',
                  }}
                >
                  <strong>EMService V/Emil Mortensen</strong>
                  <br />
                  CVR-nr.: 44220695
                  <br />
                  Vognmandsvej 1, 4480 Store Fuglede
                  <br />
                  E-mail: Emilbeiner@gmail.com
                </div>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  2. Formål & Retsgrundlag
                </h3>
                <p>Vi indsamler og behandler udelukkende personoplysninger til følgende formål:</p>
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                  <li>
                    <strong>Brugeroprettelse og godkendelse:</strong> E-mail og navn til login,
                    abonnementsstyring og 2-faktor sikkerhed jf. GDPR art. 6, stk. 1, litra b.
                  </li>
                  <li>
                    <strong>Revir- og markadministration:</strong> Geometriske polygoner, marknavne,
                    plejeplaner og biotopplaceringer for at levere platformens kerneydelse.
                  </li>
                  <li>
                    <strong>Anonym statistik (valgfrit samtykke):</strong> Aggregeret tælling af
                    sidevisninger for at optimere serverhastighed og rette kortfejl (kun ved
                    udtrykkeligt samtykke jf. GDPR art. 6, stk. 1, litra a).
                  </li>
                  <li>
                    <strong>Påmindelser og råd på mail (valgfrit samtykke):</strong> Din e-mail og
                    dit svar bruges kun til at sende dig påmindelser og råd, hvis du har sagt ja. Du
                    kan til enhver tid sige nej igen under Din profil.
                  </li>
                  <li>
                    <strong>Mårhundekort (frivillig indberetning):</strong> Position, dato og
                    eventuelt et foto gemmes sammen med din konto, så du kan se og slette dine
                    indberetninger. Andre brugere ser kun antal i områder på 2 × 2 km, aldrig din
                    præcise position, dit navn eller klokkeslæt. Fotos renses for metadata og er kun
                    synlige for dig og administratorer. Indberetninger gemmes, til du sletter dem
                    eller din konto.
                  </li>
                </ul>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  3. Opbevaring & Hosting i EU
                </h3>
                <p>
                  Alle databaser og cloud-tjenester hostes i Den Europæiske Union (Frankfurt,
                  Tyskland) via Supabase Cloud med streng Row Level Security (RLS). Dine marker,
                  planer og kontooplysninger forlader ikke EU. Data overføres krypteret via
                  TLS/HTTPS og opbevares krypteret i hvile.
                </p>
                <p>
                  <strong>Kort:</strong> Kortbilleder hentes fra kortleverandører, bl.a.
                  Dataforsyningen/Klimadatastyrelsen, OpenStreetMap og Esri (USA, kun ved
                  satellitvisning). Som ved al webbrowsing modtager de din IP-adresse og oplysning
                  om, hvilket kortudsnit der vises. De får <strong>ikke</strong> adgang til dine
                  marker, planer, navn eller konto.
                </p>
              </section>

              <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  4. Ingen Videresalg af Data
                </h3>
                <p>
                  Dine markgrænser, jagtrevirer og personlige data er strengt fortrolige.{' '}
                  <strong>Vi sælger eller udlejer aldrig</strong> dine oplysninger, og de bruges
                  ikke til annoncering. Vi bruger kun nødvendige leverandører (hosting og kort) til
                  at drive tjenesten, og de har ikke adgang til dit indhold ud over det, der er
                  beskrevet her.
                </p>
              </section>

              <section style={{ marginBottom: '10px' }}>
                <h3 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 600 }}>
                  5. Dine Rettigheder
                </h3>
                <p>Efter GDPR har du en række rettigheder i forhold til vores behandling:</p>
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                  <li>Ret til indsigt i de oplysninger, vi behandler om dig.</li>
                  <li>Ret til berigtigelse af ukorrekte oplysninger.</li>
                  <li>Ret til sletning (»retten til at blive glemt«).</li>
                  <li>Ret til dataportabilitet (eksport af markdata via GeoJSON/DXF).</li>
                  <li>Ret til at tilbagekalde dit samtykke til anonym statistik.</li>
                </ul>
                <p>
                  Hvis du ønsker at gøre brug af dine rettigheder, kan du kontakte os direkte på{' '}
                  <a href="mailto:Emilbeiner@gmail.com" style={{ color: '#4ade80' }}>
                    Emilbeiner@gmail.com
                  </a>
                  .
                </p>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#14251a',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            EMService V/Emil Mortensen · CVR: 44220695 · Retten i Holbæk
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              minHeight: '44px',
              padding: '0 20px',
              borderRadius: '8px',
              backgroundColor: '#16a34a',
              border: '1px solid #22c55e',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.88rem',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            Forstået & Luk
          </button>
        </div>
      </div>
    </div>
  );
}
